import { ReactNode, useMemo } from 'react';

import { CmsApiBlock } from 'modules/cms/api/types/CmsApiBlock';
import { getPrevRealBlock } from 'modules/cms/helpers';
import { MappedContentBlock } from 'modules/cms/mappers/MappedContentBlock';

import { CmsBlockContainerBox } from './CmsBlockContainer.styled';
import { CmsBlockContainerGrid } from './CmsBlockContainerGrid';

type Props<TBlock extends CmsApiBlock> = {
  id?: string;
  mappedBlocks: MappedContentBlock<CmsApiBlock>[];
  mappedBlock: MappedContentBlock<TBlock>;
  index: number;
  fullWidthLayout: boolean;
  showHeadingDivider: boolean;
  hideFirstHeadingDivider: boolean;
  withSidebar: boolean;
  children: ReactNode;
};

export function CmsBlockContainer<TBlock extends CmsApiBlock>({
  id,
  mappedBlocks,
  mappedBlock,
  index,
  fullWidthLayout,
  showHeadingDivider,
  hideFirstHeadingDivider,
  withSidebar,
  children,
}: Props<TBlock>) {
  const padding = useMemo(
    () =>
      fullWidthLayout ? [index === 0 ? 36 : 64, null, 70] : [40, null, 60],
    [fullWidthLayout, index]
  );

  const prevBlock = getPrevRealBlock(mappedBlocks, index);

  const display = useMemo(() => {
    if (mappedBlock.isHidden) return 'none';
    if (mappedBlock.isInline) return 'inline';
    return null;
  }, [mappedBlock.isHidden, mappedBlock.isInline]);

  const mt = useMemo(() => {
    if (withSidebar && index === 0) return [0, 0, 0];
    if (!mappedBlock.padding || mappedBlock.padding === 'DEFAULT')
      return padding;
    return 0;
  }, [mappedBlock.padding, index, padding, withSidebar]);

  const overflow = useMemo(() => {
    if (
      mappedBlock.block.type === 'SPLIT_CONTENT' &&
      mappedBlock.block.data.appearance === 'FULL_WIDTH'
    ) {
      return 'hidden';
    }

    return undefined;
  }, [mappedBlock]);

  return (
    <CmsBlockContainerBox
      id={id}
      position="relative"
      display={display}
      overflow={overflow}
      width="100%"
      mb={
        !mappedBlock.padding || mappedBlock.padding === 'DEFAULT' ? padding : 0
      }
      mt={mt}
      py={mappedBlock.hasBackground ? padding : undefined}
      pt={
        index > 0 && mappedBlock.hasBackground && prevBlock?.hasBackground
          ? [0, 0, 0]
          : undefined
      }
      $showHeadingDivider={showHeadingDivider}
      $hideFirstHeadingDivider={hideFirstHeadingDivider}
      $fullWidthLayout={fullWidthLayout}
      $hasBackground={mappedBlock.hasBackground}
      $type={mappedBlock.type}
    >
      <CmsBlockContainerGrid
        blockType={mappedBlock.type}
        blockWidth={mappedBlock.width}
        fullWidthLayout={fullWidthLayout}
        withSidebar={withSidebar}
      >
        {children}
      </CmsBlockContainerGrid>
    </CmsBlockContainerBox>
  );
}
